<script setup>
/**
 * WHAT YOUR SYSTEM ANSWERS — a Call's three settings that read the owner's own system's answer
 * (P-10.6, P-10.21), on the what-if screen under "if it didn't go through".
 *
 *  - "Your system's reason": where it writes why it said no. We send that to the customer word
 *    for word, never a made-up one.
 *  - "Send your system's own message to the customer": its confirmation or refusal, as written.
 *  - "If your system answers": one field, up to four answers, each going to a step. Anything
 *    else goes on as when it works.
 *
 * P-2.11: when the connection has a sample (its last good test's answer), each path box offers
 * the sample's fields as chips to pick from, so the owner picks `message` rather than guessing
 * how her system spells it. With no sample it says how to get one, and the boxes still type.
 *
 * What is half-typed lives here; only a complete setting reaches the step
 * (`helpers/task-system-answer.js`), because the server refuses a half one.
 */
import { computed, ref } from 'vue';

import Switch from 'dashboard/components-next/switch/Switch.vue';

import SamplePathChips from './SamplePathChips.vue';
import TaskStepLink from '../TaskStepLink.vue';
import WasloInput from '../WasloInput.vue';
import { useWasloI18n } from '../../i18n.js';
import {
  MAX_ANSWER_CASES,
  answersDraftOf,
  withAnswers,
  withReasonPath,
  withSayResult,
} from '../../helpers/task-system-answer.js';

const props = defineProps({
  /** The Call step. */
  step: { type: Object, required: true },
  /** `[{ id, name, type }]`, for "go to". */
  steps: { type: Array, default: () => [] },
  /** P-2.11: the fields of the connection's sample (`samplePaths`); `[]` for no sample. */
  samplePaths: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:step']);

const { t } = useWasloI18n();
const key = name => `WASLO.TASKS.COMPOSE.WHATIF.SYSTEM.${name}`;

const say = ref({
  on: props.step.sayResult !== undefined,
  path: props.step.sayResult?.path ?? '',
  pathAr: props.step.sayResult?.pathAr ?? '',
  when: props.step.sayResult?.on ?? 'worked',
});
const answers = ref(answersDraftOf(props.step));
const field = computed(() => answers.value.path.trim() || t(key('FIELD_NAME')));

const writeSay = changes => {
  say.value = { ...say.value, ...changes };
  const s = say.value;
  emit(
    'update:step',
    withSayResult(
      props.step,
      s.on ? { path: s.path, pathAr: s.pathAr, on: s.when } : null
    )
  );
};

const writeAnswers = next => {
  answers.value = next;
  emit('update:step', withAnswers(props.step, next));
};
const setPath = path => writeAnswers({ ...answers.value, path });
const setCase = (index, changes) =>
  writeAnswers({
    ...answers.value,
    cases: answers.value.cases.map((c, i) =>
      i === index ? { ...c, ...changes } : c
    ),
  });
const addCase = () =>
  writeAnswers({
    ...answers.value,
    cases: [...answers.value.cases, { values: '', next: '' }],
  });
const removeCase = index =>
  writeAnswers({
    ...answers.value,
    cases: answers.value.cases.filter((_, i) => i !== index),
  });

const WHEN = ['worked', 'failed', 'both'];
const WHEN_KEY = {
  worked: 'SAY_WORKED',
  failed: 'SAY_FAILED',
  both: 'SAY_BOTH',
};
</script>

<template>
  <div
    class="flex flex-col gap-4 p-4 rounded-xl bg-n-alpha-1"
    data-waslo-system-answer
  >
    <p
      v-if="!samplePaths.length"
      class="mb-0 text-xs text-n-slate-11"
      data-waslo-system-no-sample
    >
      {{ t(key('NO_SAMPLE')) }}
    </p>
    <WasloInput
      :model-value="step.reasonPath ?? ''"
      :label="t(key('REASON'))"
      :hint="t(key('REASON_HINT'))"
      placeholder="error.message"
      data-waslo-system-reason
      @update:model-value="
        value => emit('update:step', withReasonPath(step, value))
      "
    />
    <SamplePathChips
      :paths="samplePaths"
      :current="step.reasonPath ?? ''"
      data-waslo-system-reason-pick
      @pick="path => emit('update:step', withReasonPath(step, path))"
    />

    <div class="flex flex-col gap-3" data-waslo-system-say>
      <label class="flex items-start gap-3 cursor-pointer">
        <Switch
          class="mt-0.5"
          :model-value="say.on"
          data-waslo-system-say-toggle
          @update:model-value="value => writeSay({ on: value })"
        />
        <span class="text-sm text-n-slate-12">{{ t(key('SAY')) }}</span>
      </label>
      <template v-if="say.on">
        <WasloInput
          :model-value="say.path"
          :label="t(key('SAY_PATH'))"
          placeholder="message"
          data-waslo-system-say-path
          @update:model-value="value => writeSay({ path: value })"
        />
        <SamplePathChips
          :paths="samplePaths"
          :current="say.path"
          data-waslo-system-say-path-pick
          @pick="path => writeSay({ path })"
        />
        <WasloInput
          :model-value="say.pathAr"
          :label="t(key('SAY_PATH_AR'))"
          placeholder="message_ar"
          data-waslo-system-say-path-ar
          @update:model-value="value => writeSay({ pathAr: value })"
        />
        <SamplePathChips
          :paths="samplePaths"
          :current="say.pathAr"
          data-waslo-system-say-path-ar-pick
          @pick="path => writeSay({ pathAr: path })"
        />
        <div
          class="flex flex-wrap gap-2"
          role="radiogroup"
          :aria-label="t(key('SAY_ON'))"
        >
          <button
            v-for="when in WHEN"
            :key="when"
            type="button"
            role="radio"
            :aria-checked="say.when === when"
            class="px-3 py-1 text-sm rounded-lg"
            :class="
              say.when === when
                ? 'bg-n-blue-3 text-n-blue-11'
                : 'bg-n-alpha-2 text-n-slate-11'
            "
            :data-waslo-system-say-on="when"
            @click="writeSay({ when })"
          >
            {{ t(key(WHEN_KEY[when])) }}
          </button>
        </div>
      </template>
    </div>

    <div class="flex flex-col gap-3" data-waslo-system-answers>
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('ANSWERS')) }}
      </p>
      <WasloInput
        :model-value="answers.path"
        :label="t(key('ANSWERS_FIELD'))"
        placeholder="status"
        data-waslo-system-answers-field
        @update:model-value="setPath"
      />
      <SamplePathChips
        :paths="samplePaths"
        :current="answers.path"
        data-waslo-system-answers-field-pick
        @pick="setPath"
      />
      <div
        v-for="(row, index) in answers.cases"
        :key="index"
        class="flex flex-wrap items-end gap-2"
        data-waslo-system-case
      >
        <div class="flex-1 min-w-[10rem]">
          <WasloInput
            :model-value="row.values"
            :label="t(key('CASE_IS'), { field })"
            :hint="t(key('CASE_HINT'))"
            data-waslo-system-case-values
            @update:model-value="value => setCase(index, { values: value })"
          />
        </div>
        <div class="flex-1 min-w-[10rem]">
          <TaskStepLink
            :model-value="row.next"
            :label="t(key('CASE_GO'))"
            :steps="steps"
            :exclude="step.id"
            :allow-empty="false"
            data-waslo-system-case-next
            @update:model-value="value => setCase(index, { next: value })"
          />
        </div>
        <button
          type="button"
          class="mb-2 text-sm underline text-n-slate-11"
          data-waslo-system-case-remove
          @click="removeCase(index)"
        >
          {{ t(key('REMOVE_CASE')) }}
        </button>
      </div>
      <button
        v-if="answers.cases.length < MAX_ANSWER_CASES"
        type="button"
        class="self-start text-sm underline text-n-blue-11"
        data-waslo-system-case-add
        @click="addCase"
      >
        {{ t(key('ADD_CASE')) }}
      </button>
      <p class="mb-0 text-sm text-n-slate-11">{{ t(key('ELSE')) }}</p>
    </div>
  </div>
</template>
